// recanvass.ts — client wrappers for the re-canvass Call List (Aug 20, 2026).
//
// The list surfaces leads whose address has taken a NEW >=1" hail hit AFTER the
// deal went cold — the six terminal stages (no thanks / insufficient damage /
// closed lost / claims denied / cancelled / closed inspection), plus (Aug 31,
// 2026) LIVE deals sitting in `waiting` for 90+ days. A waiting lead is still
// someone's, which is why booking one splits rather than takes over. The ASM assigns batches to RMs; the
// RM calls, then either books a drone inspection (which REVIVES the existing
// deal — no duplicate) or marks no-thanks.
//
// Everything goes through SECURITY DEFINER RPCs on purpose: deals_rm_read
// scopes an RM to own+split deals, so an RM assigned a lead another RM
// originally owned reads nothing back through PostgREST. Do NOT "optimize"
// these into direct .from("deals") selects — that reintroduces the Jul 14
// cross-RLS blindness bug.

import { supabase } from "./supabase";

export interface RecanvassLead {
  lead_id: string;
  deal_id: string;
  homeowner_name: string | null;
  homeowner_phone: string | null;
  homeowner_phone_alt: string | null;
  homeowner_email: string | null;
  property_street: string | null;
  property_city: string | null;
  property_state: string | null;
  property_zip: string | null;
  property_lat: number | null;
  property_lon: number | null;
  insurance_company: string | null;
  hail_occurred_at: string;
  hail_size_in: number;
  hail_distance_m: number | null;
  dead_stage: string | null;
  dead_at: string | null;
  status: RecanvassStatus;
  assigned_rm_id: string | null;
  assigned_rm_name: string | null;
  assigned_at: string | null;
  orig_rm_id: string | null;
  orig_rm_name: string | null;
  /** Is the deal's owner still on the roster? On an RM's own lead this decides
   *  whether booking makes you the split RM (owner active) or hands you the
   *  deal outright (owner gone). Canvasser leads ignore it: see canvasser_lead. */
  orig_rm_active: boolean;
  last_outcome: string | null;
  last_outcome_at: string | null;
  attempt_count: number;
  last_call_at: string | null;
  last_text_at: string | null;
  last_inbound_at: string | null;
  total_count: number;
  /** A canvasser brought this lead in (canvas-tool appointment, a canvasser
   *  named as lead source, or "Canvass Team"). Whoever books it owns it
   *  alone, and the 5-point canvasser carve-out applies (Oct 19, 2026). */
  canvasser_lead: boolean;
  /** Who brought it in: a canvasser's name, or "Canvass Team". */
  source_canvasser_name: string | null;
  /** The deal's current split RM (null when none, or when it's the owner). */
  orig_split_id: string | null;
  orig_split_name: string | null;
}

export type RecanvassStatus =
  | "new" | "assigned" | "working" | "appt_set"
  | "no_thanks" | "bad_number" | "dnc" | "stale";

export type RecanvassOutcome =
  | "no_answer" | "left_vm" | "callback" | "interested"
  | "note" | "no_thanks" | "bad_number" | "dnc" | "already_roofed";

export interface RecanvassFilters {
  mine?: boolean;
  status?: RecanvassStatus[] | null;
  assigned?: string[] | null;
  unassigned?: boolean;
  city?: string | null;
  zip?: string | null;
  minSize?: number | null;
  hailFrom?: string | null;
  hailTo?: string | null;
  search?: string | null;
  sort?: "hail_desc" | "size_desc" | "dead_asc" | "name" | "city";
  limit?: number;
  offset?: number;
}

export interface RecanvassSummary {
  unassigned: number;
  assigned: number;
  working: number;
  appt_set: number;
  no_thanks: number;
  mine: number;
  by_rm: Array<{ rm_id: string; name: string; open: number; appt_set: number; no_thanks: number }>;
  can_assign: boolean;
  me: string | null;
  /* access control (Aug 31, 2026) — every count above is already scoped to what
     the caller may actually open, so the chips can't promise leads the server
     will refuse to hand over. */
  enabled: boolean;
  /** The board is switched off AND the viewer is in the field tier. */
  paused_for_me: boolean;
  /** An allowlist exists AND the viewer is in the field tier. */
  zip_limited: boolean;
  allowed_zips: string[];
  can_manage_access: boolean;
  /** Viewer is a Restore Manager or Team Lead — the roles the controls govern. */
  subject_to_access_control: boolean;
}

/* ── access control: the on/off switch + the approved-ZIP allowlist ──────────
 *
 * Alex's ask (Aug 31, 2026): a switch so the team can't abuse the Call List,
 * and a set of ZIPs he approves that reps may call — "so our drone pilot is not
 * running all over Atlanta."
 *
 * Both are enforced in the SECURITY DEFINER RPCs, not here. This file only
 * renders the state. AN EMPTY ALLOWLIST MEANS NO RESTRICTION — that is what
 * keeps the feature a no-op until Alex approves his first ZIP. */

export interface RecanvassZipStat {
  zip: string;
  cities: string[];
  active: number;
  unassigned: number;
  max_hail: number | null;
}

export interface RecanvassApprovedZip {
  zip: string;
  added_at: string;
  added_by_name: string | null;
}

export interface RecanvassAccess {
  enabled: boolean;
  enabled_at: string | null;
  enabled_by_name: string | null;
  can_manage: boolean;
  subject_to_access_control: boolean;
  allowed_zips: RecanvassApprovedZip[];
  /** Every ZIP in the live pool with counts — the picker shows what each
   *  approval actually opens up. */
  zip_stats: RecanvassZipStat[];
  /** Leads with no ZIP on file. They can never match an allowlist, so once one
   *  exists they are invisible to reps. Surfaced rather than silently dropped. */
  no_zip_leads: number;
}

export interface RecanvassTrailRow {
  kind: "activity" | "call" | "text";
  action: string | null;
  detail: string | null;
  actor_name: string | null;
  occurred_at: string;
}

/** Human labels — kept here so the board, the trail, and any future export agree. */
export const OUTCOME_LABELS: Record<RecanvassOutcome, string> = {
  no_answer: "No answer",
  left_vm: "Left voicemail",
  callback: "Call back later",
  interested: "Interested",
  note: "Note",
  no_thanks: "No thanks",
  bad_number: "Bad number",
  dnc: "Do not contact",
  already_roofed: "Already re-roofed",
};

export const STATUS_LABELS: Record<RecanvassStatus, string> = {
  new: "Needs an RM",
  assigned: "Assigned",
  working: "Working",
  appt_set: "Appointment set",
  no_thanks: "No thanks",
  bad_number: "Bad number",
  dnc: "Do not contact",
  stale: "No longer qualifies",
};

export const DEAD_STAGE_LABELS: Record<string, string> = {
  no_thanks: "No thanks",
  insufficient_damage: "Insufficient damage",
  closed_lost: "Closed lost",
  claims_denied: "Claim denied",
  cancelled: "Cancelled",
  closed_inspection: "Closed inspection",
  waiting: "Waiting — quiet 90d+",
};

export async function fetchRecanvassLeads(f: RecanvassFilters = {}): Promise<RecanvassLead[]> {
  const { data, error } = await supabase.rpc("recanvass_list", {
    p_mine: f.mine ?? false,
    p_status: f.status ?? null,
    p_assigned: f.assigned ?? null,
    p_unassigned: f.unassigned ?? false,
    p_city: f.city ?? null,
    p_zip: f.zip ?? null,
    p_min_size: f.minSize ?? null,
    p_hail_from: f.hailFrom ?? null,
    p_hail_to: f.hailTo ?? null,
    p_search: f.search ?? null,
    p_sort: f.sort ?? "hail_desc",
    p_limit: f.limit ?? 200,
    p_offset: f.offset ?? 0,
  });
  if (error) throw error;
  return (data ?? []) as RecanvassLead[];
}

export async function fetchRecanvassSummary(): Promise<RecanvassSummary> {
  const { data, error } = await supabase.rpc("recanvass_summary");
  if (error) throw error;
  return data as RecanvassSummary;
}

export async function fetchRecanvassTrail(leadId: string): Promise<RecanvassTrailRow[]> {
  const { data, error } = await supabase.rpc("recanvass_lead_trail", { p_lead_id: leadId });
  if (error) throw error;
  return (data ?? []) as RecanvassTrailRow[];
}

/** Assign a batch to one RM. Pass null to return them to the unassigned pool. */
export async function assignRecanvassLeads(leadIds: string[], rmId: string | null) {
  const { data, error } = await supabase.rpc("recanvass_assign", {
    p_lead_ids: leadIds,
    p_rm_id: rmId,
  });
  if (error) throw error;
  return data as {
    ok: boolean; assigned: number; rm_id: string | null; rm_name: string | null;
    skipped?: number;
    /** Leads dropped because the ASSIGNEE isn't cleared for that ZIP. Handing a
     *  rep a lead they can't open would bury it in a queue they can't work. */
    skipped_zip?: number;
  };
}

/** Deal the selected leads round-robin across several RMs — the "filter it, then
 *  split it across the team" flow. Returns per-RM counts. */
export async function splitRecanvassLeads(leadIds: string[], rmIds: string[]) {
  const buckets: Record<string, string[]> = {};
  rmIds.forEach((id) => { buckets[id] = []; });
  leadIds.forEach((leadId, i) => { buckets[rmIds[i % rmIds.length]].push(leadId); });
  const out: Array<{ rm_id: string; rm_name: string | null; assigned: number }> = [];
  for (const rmId of rmIds) {
    if (!buckets[rmId].length) continue;
    const r = await assignRecanvassLeads(buckets[rmId], rmId);
    out.push({ rm_id: rmId, rm_name: r.rm_name, assigned: r.assigned });
  }
  return out;
}

export async function logRecanvassOutcome(leadId: string, outcome: RecanvassOutcome, note?: string) {
  const { data, error } = await supabase.rpc("recanvass_log_outcome", {
    p_lead_id: leadId,
    p_outcome: outcome,
    p_note: note?.trim() || null,
  });
  if (error) throw error;
  return data as { ok: boolean; lead_id: string; status: RecanvassStatus };
}

/** Book the drone inspection — revives the existing deal to appointment_received,
 *  stamps the new hail date as date_of_loss, and releases the knock pin's DNK. */
export async function scheduleRecanvassAppointment(args: {
  leadId: string;
  appointmentAt: string;
  ownerId?: string | null;
  phone?: string | null;
  email?: string | null;
  insurance?: string | null;
  notes?: string | null;
}) {
  const { data, error } = await supabase.rpc("recanvass_schedule_appointment", {
    p_lead_id: args.leadId,
    p_appointment_at: args.appointmentAt,
    p_owner_id: args.ownerId ?? null,
    p_phone: args.phone?.trim() || null,
    p_email: args.email?.trim() || null,
    p_insurance: args.insurance?.trim() || null,
    p_notes: args.notes?.trim() || null,
  });
  if (error) throw error;
  return data as {
    ok: boolean; deal_id: string; owner_id: string; prev_stage: string;
    mode: BookingMode;
    split_rm_id: string | null;
    credited_name: string | null;
    displaced_split_id: string | null;
    displaced_split_name: string | null;
    /** Canvasser lead only: the active owner who came off the deal. */
    replaced_owner_id: string | null;
    replaced_owner_name: string | null;
    canvasser_lead: boolean;
    /** Who the carve-out is credited to, on a canvasser lead. */
    canvasser_name: string | null;
  };
}

/** How a Call List booking lands (Greg, Oct 19, 2026):
 *  'canvasser' = a canvasser brought the lead in. The RM who books owns it
 *                alone (any active owner and split RM come off) and the
 *                5-point canvasser carve-out applies.
 *  'split'     = an RM's own lead whose owner is still active. They keep it
 *                and the RM who books takes the split seat. No carve-out.
 *  'owner'     = an RM's own lead nobody active owns (or the owner is
 *                booking). The RM who books owns it. No carve-out. */
export type BookingMode = "canvasser" | "split" | "owner";

/** Mirrors the server's rule in recanvass_schedule_appointment so the modal can
 *  say what will happen BEFORE the click. The server still decides — this only
 *  has to agree with it. creditId is who the booking credits: the modal's
 *  picker, else the rep the lead is assigned to, else the person clicking. */
export function predictBookingMode(
  lead: Pick<RecanvassLead, "orig_rm_id" | "orig_rm_active" | "canvasser_lead">,
  creditId: string | null,
): BookingMode {
  if (lead.canvasser_lead) return "canvasser";
  if (!lead.orig_rm_active || !lead.orig_rm_id) return "owner";
  if (!creditId) return "owner";
  return creditId === lead.orig_rm_id ? "owner" : "split";
}

/** Miles, for display. Hail distance is stored in meters. */
export function metersToMiles(m: number | null | undefined): number | null {
  if (m == null) return null;
  return Math.round((m / 1609.344) * 10) / 10;
}

/* ── access control wrappers ─────────────────────────────────────────────── */

export async function fetchRecanvassAccess(): Promise<RecanvassAccess> {
  const { data, error } = await supabase.rpc("recanvass_access_settings");
  if (error) throw error;
  return data as RecanvassAccess;
}

/** Master switch. OFF hides the board from Restore Managers and Team Leads and
 *  refuses their bookings; managers keep full access so whoever switched it off
 *  can always switch it back on. */
export async function setRecanvassEnabled(enabled: boolean) {
  const { data, error } = await supabase.rpc("recanvass_set_enabled", { p_enabled: enabled });
  if (error) throw error;
  return data as { ok: boolean; enabled: boolean };
}

/** Replaces the whole allowlist in one call, so "these are the approved ZIPs" is
 *  a single decision. Pass [] to lift the restriction entirely. */
export async function setRecanvassAllowedZips(zips: string[]) {
  const { data, error } = await supabase.rpc("recanvass_set_allowed_zips", { p_zips: zips });
  if (error) throw error;
  return data as { ok: boolean; zips: string[]; added: string[]; removed: string[] };
}
